import { useEffect, useState, useCallback, useRef } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  InfoCard,
  EmptyState,
} from '@backstage/core-components';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import RefreshIcon from '@material-ui/icons/Refresh';
import ReplayIcon from '@material-ui/icons/Replay';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { parse as parseYaml } from 'yaml';

// ── Types ──────────────────────────────────────────────────────────────────

type Run = {
  id: number;
  name?: string;
  title?: string;
  display_title?: string;
  path?: string;          // Gitea: "workflow-file.yml@refs/heads/branch"
  workflow_name?: string;
  workflow_id?: string;
  index?: number;
  status: string;
  conclusion?: string;
  event?: string;
  trigger_event?: string;
  html_url?: string;
  url?: string;
  created_at?: string;
  created?: string;
  updated_at?: string;
  updated?: string;
  run_number?: number;
  run_started_at?: string;
  started?: string;
  started_at?: string;
  stopped?: string;
  stopped_at?: string;
  head_branch?: string;
  head_sha?: string;
  commit_sha?: string;
  head_commit?: { id?: string; message?: string; author?: { name?: string } };
  trigger_user?: { login?: string; full_name?: string; email?: string };
  trigger_actor?: { login?: string; full_name?: string };
  actor?: { login?: string; full_name?: string };
};

type StepDef = { name?: string; uses?: string; run?: string };
type StepStatus = { number: number; name: string; status: string; conclusion?: string };

type Job = {
  id: number;
  name: string;
  status: string;
  conclusion?: string;
  started_at?: string;
  completed_at?: string;
  html_url?: string;
  steps?: StepStatus[];
};

type WorkflowJobDef = { name?: string; needs?: string | string[]; steps?: StepDef[] };

type WorkflowDef = {
  filename: string;
  displayName: string;
  triggers: string[];
  jobs: Record<string, WorkflowJobDef>;
};

// ── Field accessors (handle both GitHub-style and Gitea-style) ─────────────

const runNumber  = (r: Run) => r.run_number ?? r.index ?? r.id;
const headSha    = (r: Run) => r.head_sha ?? r.commit_sha;
const runEvent   = (r: Run) => r.trigger_event ?? r.event ?? 'push';
const startedAt  = (r: Run) => r.run_started_at ?? r.started_at ?? r.started ?? r.created_at ?? r.created;
// For concluded runs, updated_at/updated is Gitea's de-facto finish time.
// For active/unknown runs pass undefined so formatDuration can use Date.now().
const stoppedAt  = (r: Run) => {
  if (r.stopped_at) return r.stopped_at;
  if (r.stopped) return r.stopped;
  const done = r.conclusion && r.conclusion !== 'null' && r.conclusion !== '';
  if (done) return r.updated_at ?? r.updated ?? undefined;
  return undefined;
};
const createdAt  = (r: Run) => r.created_at ?? r.created;
const htmlUrl    = (r: Run) => r.html_url ?? r.url;
const triggeredBy = (r: Run) =>
  r.actor?.full_name ??
  r.actor?.login ??
  r.trigger_actor?.full_name ??
  r.trigger_actor?.login ??
  r.head_commit?.author?.name ??
  r.trigger_user?.full_name ??
  r.trigger_user?.login ??
  '\u2014';

const wfNameFromRun = (r: Run): string => {
  // Gitea returns `path` like "deploy-humanitec.yml@refs/heads/main" but
  // does NOT populate workflow_id or workflow_name — extract from path first.
  if (r.path) return r.path.split('@')[0];
  return (
    r.workflow_name ??
    r.workflow_id ??
    (r.name && r.name.trim() !== '' ? r.name : undefined) ??
    r.title ??
    'unknown'
  );
};

function runMatchesWorkflow(run: Run, wf: WorkflowDef): boolean {
  const rn = wfNameFromRun(run).toLowerCase();
  if (rn === 'unknown') {
    // fallback: match by trigger event
    return wf.triggers.includes(runEvent(run));
  }
  return (
    rn === wf.filename.toLowerCase() ||
    rn === wf.filename.replace(/\.ya?ml$/i, '').toLowerCase() ||
    rn === wf.displayName.toLowerCase()
  );
}

// ── Utilities ─────────────────────────────────────────────────────────────

function getRepoSlug(annotations: Record<string, string> | undefined) {
  return annotations?.['gitea.kyndemo.live/repo-slug'];
}

export const isGiteaActionsAvailable = (entity: any) =>
  Boolean(getRepoSlug(entity?.metadata?.annotations));

function formatDuration(start?: string, end?: string, active = false): string {
  if (!start) return '\u2014';
  const s = new Date(start).getTime();
  // For non-active runs without a known end time, show — rather than wrong value
  const e = end ? new Date(end).getTime() : (active ? Date.now() : null);
  if (e === null) return '\u2014';
  const sec = Math.floor((e - s) / 1000);
  if (sec < 0) return '\u2014';
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ${sec % 60}s`;
  return `${Math.floor(min / 60)}h ${min % 60}m`;
}

function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return '\u2014';
  const diff = Date.now() - new Date(dateStr).getTime();
  const sec = Math.floor(diff / 1000);
  const min = Math.floor(sec / 60);
  const hrs = Math.floor(min / 60);
  const days = Math.floor(hrs / 24);
  if (days > 0) return `${days}d ago`;
  if (hrs > 0) return `${hrs}h ago`;
  if (min > 0) return `${min}m ago`;
  return `${sec}s ago`;
}

async function fetchWithRetry(url: string, options: RequestInit = {}, maxRetries = 2): Promise<Response> {
  let lastError: Error | null = null;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const resp = await fetch(url, options);
      if (resp.status === 429) {
        await new Promise(r => setTimeout(r, Math.pow(2, attempt) * 1000));
        continue;
      }
      if (resp.ok || resp.status < 500) return resp;
      throw new Error(`Server error: ${resp.status}`);
    } catch (err) {
      lastError = err as Error;
      if (attempt < maxRetries) await new Promise(r => setTimeout(r, Math.pow(2, attempt) * 1000));
    }
  }
  throw lastError ?? new Error('Max retries exceeded');
}

// ── StatusBadge ───────────────────────────────────────────────────────────

const STATUS_CFG: Record<string, { label: string; color: string }> = {
  success:   { label: 'Success',   color: '#2e7d32' },
  failure:   { label: 'Failed',    color: '#c62828' },
  running:   { label: 'Running',   color: '#1565c0' },
  in_progress: { label: 'Running', color: '#1565c0' },
  waiting:   { label: 'Queued',    color: '#6a1b9a' },
  queued:    { label: 'Queued',    color: '#6a1b9a' },
  cancelled: { label: 'Cancelled', color: '#546e7a' },
  skipped:   { label: 'Skipped',   color: '#546e7a' },
  neutral:   { label: 'Neutral',   color: '#546e7a' },
};

const StatusBadge = ({ status, conclusion }: { status: string; conclusion?: string }) => {
  // Use || so empty-string conclusion falls through to status
  const s = (conclusion || status);
  const cfg = STATUS_CFG[s] ?? { label: s, color: '#546e7a' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 10,
      fontSize: '0.7rem',
      fontWeight: 600,
      backgroundColor: cfg.color + '1a',
      color: cfg.color,
      border: `1px solid ${cfg.color}44`,
      whiteSpace: 'nowrap',
    }}>{cfg.label}</span>
  );
};

// ── JobFlowDiagram ────────────────────────────────────────────────────────

function computeLayers(jobs: Record<string, WorkflowJobDef>): string[][] {
  const layers: string[][] = [];
  const placed = new Set<string>();
  const remaining = new Set(Object.keys(jobs));
  while (remaining.size > 0) {
    const layer: string[] = [];
    for (const id of remaining) {
      const needs = Array.isArray(jobs[id].needs)
        ? (jobs[id].needs as string[])
        : jobs[id].needs ? [jobs[id].needs as string] : [];
      if (needs.every(n => placed.has(n))) layer.push(id);
    }
    if (layer.length === 0) break;
    layer.forEach(id => { remaining.delete(id); placed.add(id); });
    layers.push(layer);
  }
  return layers;
}

const jobStatusColor = (status?: string) => {
  if (status === 'success') return '#4caf50';
  if (status === 'failure') return '#f44336';
  if (status === 'running' || status === 'in_progress') return '#2196f3';
  if (status === 'skipped') return '#9e9e9e';
  if (status === 'cancelled') return '#ff9800';
  return undefined; // no status yet
};

function JobFlowDiagram({
  jobs,
  jobStatuses,
  jobStepStatuses,
  diagramId,
}: {
  jobs: Record<string, WorkflowJobDef>;
  jobStatuses?: Record<string, string>;
  jobStepStatuses?: Record<string, StepStatus[]>;
  diagramId: string;
}) {
  const theme = useTheme();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(800);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    setContainerW(el.clientWidth || 800);
    const ro = new ResizeObserver(entries => setContainerW(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const defaultClr = theme.palette.type === 'dark' ? '#90caf9' : '#1976d2';
  const textClr    = theme.palette.type === 'dark' ? '#ccc' : '#555';

  // Layout constants sized so text matches Backstage body (~12-13px).
  // MAX_SCALE caps upscaling so fonts never blow up on sparse workflows.
  const JH_HEADER    = 28;  // job header row height
  const STEP_ROW_H   = 20;  // step row height
  const STEP_BOT_PAD = 5;   // padding below step row
  const STEP_GAP     = 5;   // gap between step slots
  const H_PAD        = 10;  // inner horizontal padding
  const JW_MIN       = 140; // minimum job box width
  const H_GAP        = 44;  // gap between layer columns
  const V_GAP        = 10;  // gap between jobs in same column
  const PAD          = 14;  // outer diagram padding

  // Prefer runtime step statuses (from API) over YAML-defined steps
  const stepListFor = (jobId: string): (StepStatus | StepDef)[] =>
    jobStepStatuses?.[jobId] ?? jobs[jobId]?.steps ?? [];

  // Natural job box width — steps get at least 78px each, min box JW_MIN
  const STEP_W_MIN = 78;
  const jw = (jobId: string) => {
    const n = stepListFor(jobId).length;
    if (n === 0) return JW_MIN;
    return Math.max(JW_MIN, H_PAD * 2 + n * (STEP_W_MIN + STEP_GAP) - STEP_GAP);
  };

  // Normalized step slot width: distribute job box space evenly across steps
  const stepSlotW = (jobId: string): number => {
    const n = stepListFor(jobId).length;
    if (n === 0) return STEP_W_MIN;
    return Math.floor((jw(jobId) - H_PAD * 2 + STEP_GAP) / n - STEP_GAP);
  };

  // Job box height: header + optional single step row
  const jh = (jobId: string) => {
    const hasSteps = stepListFor(jobId).length > 0;
    return JH_HEADER + (hasSteps ? STEP_ROW_H + STEP_BOT_PAD : 0);
  };

  const layers = computeLayers(jobs);
  if (layers.length === 0) return null;

  // Each layer's column width = widest job in that layer
  const layerW = (layer: string[]) => Math.max(...layer.map(jw));

  const layerH = (layer: string[]) =>
    layer.reduce((s, id) => s + jh(id), 0) + Math.max(0, layer.length - 1) * V_GAP;
  const maxLayerH = Math.max(...layers.map(layerH));

  // Cumulative x position per layer
  const layerXs: number[] = [];
  let cx = PAD;
  layers.forEach(layer => { layerXs.push(cx); cx += layerW(layer) + H_GAP; });

  const totalW = cx - H_GAP + PAD; // subtract trailing gap, add right pad
  const totalH = PAD * 2 + maxLayerH;

  const coords: Record<string, { x: number; y: number }> = {};
  layers.forEach((layer, li) => {
    const x  = layerXs[li];
    const lh = layerH(layer);
    let y = PAD + (maxLayerH - lh) / 2;
    layer.forEach(jobId => {
      coords[jobId] = { x, y };
      y += jh(jobId) + V_GAP;
    });
  });

  const arrowClr = theme.palette.type === 'dark' ? '#555' : '#bdbdbd';
  const markerId = `jf-${diagramId}`;

  // Scale up to fill container, but cap at 1.15× so fonts stay near body size
  const MAX_SCALE = 1.15;
  const scale = Math.min(containerW / totalW, MAX_SCALE);
  const svgW = Math.round(totalW * scale);
  const svgH = Math.round(totalH * scale);

  return (
    <div ref={wrapRef} style={{ width: '100%' }}>
    <svg
      viewBox={`0 0 ${totalW} ${totalH}`}
      width={svgW}
      height={svgH}
      style={{ display: 'block', overflow: 'visible' }}
    >
      <defs>
        <marker id={markerId} markerWidth="8" markerHeight="7" refX="8" refY="3.5" orient="auto">
          <path d="M0,0 L8,3.5 L0,7 Z" fill={arrowClr} />
        </marker>
      </defs>

      {/* Arrows — connect right-center of source to left-center of target */}
      {Object.entries(jobs).flatMap(([jobId, job]) => {
        const needs = Array.isArray(job.needs)
          ? (job.needs as string[])
          : job.needs ? [job.needs as string] : [];
        return needs.map(needId => {
          const from = coords[needId]; const to = coords[jobId];
          if (!from || !to) return null;
          const x1 = from.x + jw(needId); const y1 = from.y + jh(needId) / 2;
          const x2 = to.x;               const y2 = to.y + jh(jobId) / 2;
          const mid = (x1 + x2) / 2;
          return (
            <path key={`${needId}->${jobId}`}
              d={`M${x1},${y1} C${mid},${y1} ${mid},${y2} ${x2},${y2}`}
              fill="none" stroke={arrowClr} strokeWidth={1.5}
              markerEnd={`url(#${markerId})`}
            />
          );
        });
      })}

      {/* Job nodes */}
      {Object.entries(coords).map(([jobId, pos]) => {
        const job      = jobs[jobId];
        const status   = jobStatuses?.[jobId];
        const color    = jobStatusColor(status) ?? defaultClr;
        const boxW     = jw(jobId);
        const boxH     = jh(jobId);
        const label    = job.name ?? jobId;
        const maxChars = Math.max(14, Math.floor((boxW - 40) / 8));
        const short    = label.length > maxChars ? label.slice(0, maxChars - 1) + '\u2026' : label;
        const steps    = stepListFor(jobId);
        const hasSteps = steps.length > 0;

        return (
          <g key={jobId}>
            {/* Box */}
            <rect x={pos.x} y={pos.y} width={boxW} height={boxH} rx={6}
              fill={color + '15'} stroke={color} strokeWidth={1.5} />

            {/* Job header row */}
            {status && (
              <circle cx={pos.x + 13} cy={pos.y + JH_HEADER / 2} r={4} fill={color} />
            )}
            <text
              x={pos.x + (status ? 24 : 10)} y={pos.y + JH_HEADER / 2}
              dominantBaseline="middle" fontSize={11} fontWeight={700} fill={color}>
              {short}
            </text>

            {/* Separator + horizontal step row */}
            {hasSteps && (
              <>
                <line
                  x1={pos.x + 1} y1={pos.y + JH_HEADER}
                  x2={pos.x + boxW - 1} y2={pos.y + JH_HEADER}
                  stroke={color} strokeWidth={0.75} opacity={0.25}
                />
                {steps.map((step, si) => {
                  const isRuntime = 'number' in step;
                  const rawName = isRuntime
                    ? (step as StepStatus).name
                    : ((step as StepDef).name ?? (step as StepDef).uses ?? (step as StepDef).run ?? `step ${si + 1}`);
                  const stepStatus = isRuntime
                    ? ((step as StepStatus).conclusion ?? (step as StepStatus).status)
                    : undefined;
                  const sColor  = stepStatus ? (jobStatusColor(stepStatus) ?? '#9e9e9e') : textClr;
                  // Normalize slot width: distribute job box evenly across step count
                  const slotW   = stepSlotW(jobId);
                  // Truncate label to fit the normalized slot (dot ≈ 18px, rest for text)
                  const maxCh   = Math.max(4, Math.floor(Math.max(0, slotW - 18) / 7));
                  const sName   = rawName.length > maxCh ? rawName.slice(0, maxCh - 1) + '\u2026' : rawName;
                  const sx      = pos.x + H_PAD + si * (slotW + STEP_GAP);
                  const sy      = pos.y + JH_HEADER + STEP_ROW_H / 2;
                  // Pulse animation ring for running/in-progress steps
                  const isActive = stepStatus === 'running' || stepStatus === 'in_progress' || stepStatus === 'waiting';
                  return (
                    <g key={si}>
                      <title>{rawName}{stepStatus ? ` — ${stepStatus}` : ''}</title>
                      {isActive && (
                        <circle cx={sx + 7} cy={sy} r={9}
                          fill="none" stroke={sColor} strokeWidth={1} opacity={0.3} />
                      )}
                      <circle
                        cx={sx + 7} cy={sy} r={5}
                        fill={stepStatus ? sColor : 'none'}
                        stroke={sColor} strokeWidth={1.2}
                        opacity={stepStatus ? 0.9 : 0.4}
                      />
                      <text
                        x={sx + 12} y={sy}
                        dominantBaseline="middle" fontSize={9}
                        fill={stepStatus ? sColor : textClr}
                        opacity={stepStatus ? 1 : 0.6}>
                        {sName}
                      </text>
                    </g>
                  );
                })}
              </>
            )}
          </g>
        );
      })}
    </svg>
    </div>
  );
}

// ── WorkflowCard styles ───────────────────────────────────────────────────

const useCardStyles = makeStyles(theme => ({
  card: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
  },
  header: {
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    userSelect: 'none',
  },
  diagram: {
    padding: theme.spacing(2, 2, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    overflowX: 'auto',   // fallback for very small screens
    width: '100%',
  },
  cell: {
    padding: '6px 12px',
    fontSize: '0.8rem',
    borderBottom: `1px solid ${theme.palette.divider}`,
    verticalAlign: 'middle',
  },
  headCell: {
    padding: '6px 12px',
    fontSize: '0.73rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    opacity: 0.6,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

// ── WorkflowCard ──────────────────────────────────────────────────────────

function WorkflowCard({
  wf, runs, jobStatuses, jobStepStatuses, giteaBaseUrl, slug, onRerun,
}: {
  wf: WorkflowDef;
  runs: Run[];
  jobStatuses: Record<string, string>;
  jobStepStatuses: Record<string, StepStatus[]>;
  giteaBaseUrl: string;
  slug: string;
  onRerun: (run: Run) => Promise<void>;
}) {
  const classes = useCardStyles();
  const [open, setOpen] = useState(true);

  const wfRuns = runs
    .filter(r => runMatchesWorkflow(r, wf))
    .sort((a, b) => runNumber(b) - runNumber(a))
    .slice(0, 10);

  const latestRun = wfRuns[0];

  return (
    <div className={classes.card}>
      {/* Header — clickable to expand/collapse */}
      <div className={classes.header} onClick={() => setOpen(v => !v)} role="button" tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && setOpen(v => !v)}>
        <Box flexGrow={1} display="flex" alignItems="center" style={{ gap: 10, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" style={{ fontWeight: 700, lineHeight: 1.3 }}>
            {wf.displayName}
          </Typography>
          <Typography variant="caption" color="textSecondary" style={{ fontFamily: 'monospace' }}>
            {wf.filename}
          </Typography>
          {wf.triggers.map(t => (
            <Chip key={t} label={`on: ${t}`} size="small" variant="outlined"
              style={{ fontSize: '0.7rem', height: 20 }} />
          ))}
          {latestRun && (
            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
              <Typography variant="caption" color="textSecondary">latest:</Typography>
              <StatusBadge status={latestRun.status} conclusion={latestRun.conclusion} />
            </Box>
          )}
        </Box>
        <IconButton size="small" edge="end">
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </div>

      <Collapse in={open}>
        {/* Job flow diagram */}
        {Object.keys(wf.jobs).length > 0 && (
          <div className={classes.diagram}>
            <JobFlowDiagram
              jobs={wf.jobs}
              jobStatuses={Object.keys(jobStatuses).length > 0 ? jobStatuses : undefined}
              jobStepStatuses={Object.keys(jobStepStatuses).length > 0 ? jobStepStatuses : undefined}
              diagramId={wf.filename.replace(/[^a-z0-9]/gi, '-')}
            />
          </div>
        )}

        {/* Runs table */}
        {wfRuns.length > 0 ? (
          <Table size="small" style={{ tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
              <col style={{ width: 60 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 100 }} />
              <col style={{ width: 64 }} />
            </colgroup>
            <TableHead>
              <TableRow>
                {['Run', 'Commit', 'Event', 'Triggered by', 'Status', 'Duration', 'Started', ''].map(h => (
                  <TableCell key={h} className={classes.headCell}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {wfRuns.map(run => (
                <TableRow key={run.id} hover>
                  <TableCell className={classes.cell}>#{runNumber(run)}</TableCell>
                  <TableCell className={classes.cell}>
                    {headSha(run) ? (
                      <Tooltip title={run.head_commit?.message ?? ''}>
                        <Link href={`${giteaBaseUrl}/${slug}/commit/${headSha(run)}`}
                          target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'monospace' }}>
                          {headSha(run)!.slice(0, 7)}
                        </Link>
                      </Tooltip>
                    ) : '\u2014'}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <Chip label={runEvent(run)} size="small" variant="outlined"
                      style={{ fontSize: '0.7rem', height: 20 }} />
                  </TableCell>
                  <TableCell className={classes.cell}>{triggeredBy(run)}</TableCell>
                  <TableCell className={classes.cell}>
                    <StatusBadge status={run.status} conclusion={run.conclusion} />
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {formatDuration(startedAt(run), stoppedAt(run), run.status === 'running' || run.status === 'in_progress' || run.status === 'waiting')}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <Tooltip title={(() => { const t = startedAt(run) ?? createdAt(run); return t ? new Date(t).toLocaleString() : ''; })()}>
                      <span>{formatRelativeTime(startedAt(run) ?? createdAt(run))}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell className={classes.cell} style={{ whiteSpace: 'nowrap' }}>
                    {(run.status === 'failure' || run.conclusion === 'failure') && (
                      <Tooltip title="Re-run workflow">
                        <IconButton size="small" onClick={() => onRerun(run)}>
                          <ReplayIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {htmlUrl(run) && (
                      <Tooltip title="Open in Gitea">
                        <IconButton size="small" component="a" href={htmlUrl(run)!}
                          target="_blank" rel="noopener noreferrer">
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Box p={2}>
            <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
              No runs found for this workflow yet.
            </Typography>
          </Box>
        )}
      </Collapse>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

export const GiteaActionsContent = () => {
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const slug = getRepoSlug(entity.metadata.annotations);

  let giteaBaseUrl = 'https://gitea.kyndemo.live';
  try {
    const integrations = config.getOptionalConfigArray('integrations.gitea');
    if (integrations?.[0]) giteaBaseUrl = integrations[0].getOptionalString('baseUrl') ?? giteaBaseUrl;
  } catch { /* use default */ }

  const [owner, repo] = (slug ?? '/').split('/');

  const [runs,         setRuns]         = useState<Run[] | null>(null);
  const [runsError,    setRunsError]    = useState<string | null>(null);
  const [runsLoading,  setRunsLoading]  = useState(false);
  const [workflows,    setWorkflows]    = useState<WorkflowDef[] | null>(null);
  const [wfLoading,    setWfLoading]    = useState(false);
  // [workflowFilename][jobId] = status
const [wfJobStatuses,     setWfJobStatuses]     = useState<Record<string, Record<string, string>>>({});
  const [wfJobStepStatuses, setWfJobStepStatuses] = useState<Record<string, Record<string, StepStatus[]>>>({});

  // ── Fetch runs ──────────────────────────────────────────────────────────

  const fetchRuns = useCallback(async () => {
    if (!slug) return;
    setRunsLoading(true); setRunsError(null);
    try {
      const resp = await fetchWithRetry(`/api/proxy/gitea/repos/${owner}/${repo}/actions/runs`);
      if (!resp.ok) throw new Error(`Gitea API ${resp.status}`);
      const data = await resp.json();
      const list: Run[] = Array.isArray(data) ? data : (data.workflow_runs ?? data.runs ?? []);
      setRuns(list);
    } catch (e: any) {
      setRunsError(e.message); setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  }, [slug, owner, repo]);

  // ── Fetch workflow YAML definitions ────────────────────────────────────

  const fetchWorkflows = useCallback(async () => {
    if (!slug) return;
    setWfLoading(true);
    try {
      const dirResp = await fetchWithRetry(
        `/api/proxy/gitea/repos/${owner}/${repo}/contents/.gitea/workflows`
      );
      if (!dirResp.ok) { setWorkflows([]); return; }
      const files: any[] = await dirResp.json();

      const defs: WorkflowDef[] = (await Promise.all(
        files
          .filter(f => /\.ya?ml$/i.test(f.name))
          .map(async (f): Promise<WorkflowDef | null> => {
            try {
              const fileResp = await fetchWithRetry(
                `/api/proxy/gitea/repos/${owner}/${repo}/contents/${encodeURIComponent(f.path)}`
              );
              if (!fileResp.ok) return null;
              const fileData = await fileResp.json();
              const raw = atob((fileData.content as string).replace(/\s/g, ''));
              const parsed = parseYaml(raw) as any;

              const onField = parsed?.on;
              const triggers: string[] = onField
                ? (typeof onField === 'string'
                    ? [onField]
                    : typeof onField === 'object' ? Object.keys(onField) : [])
                : [];

              const jobs: Record<string, WorkflowJobDef> = {};
              for (const [id, val] of Object.entries(parsed?.jobs ?? {})) {
                const j = val as any;
                const steps: StepDef[] = (j.steps ?? []).map((s: any) => ({
                  name: s.name,
                  uses: s.uses,
                  run: typeof s.run === 'string' ? s.run.split('\n')[0].trim() : undefined,
                }));
                jobs[id] = { name: j.name, needs: j.needs, steps };
              }

              return {
                filename: f.name,
                displayName: parsed?.name ?? f.name.replace(/\.ya?ml$/i, ''),
                triggers,
                jobs,
              };
            } catch {
              return null;
            }
          })
      )).filter(Boolean) as WorkflowDef[];

      setWorkflows(defs);
    } catch {
      setWorkflows([]);
    } finally {
      setWfLoading(false);
    }
  }, [slug, owner, repo]);

  // ── Bootstrap ──────────────────────────────────────────────────────────

  useEffect(() => { fetchRuns(); fetchWorkflows(); }, [fetchRuns, fetchWorkflows]);

  // ── Poll active runs ────────────────────────────────────────────────────

  useEffect(() => {
    const active = runs?.some(r =>
      r.status === 'running' || r.status === 'waiting' || r.status === 'in_progress'
    );
    if (!active) return undefined;
    const t = setInterval(fetchRuns, 10_000);
    return () => clearInterval(t);
  }, [runs, fetchRuns]);

  // ── Fetch job statuses for latest run per workflow ──────────────────────

  useEffect(() => {
    if (!runs || !workflows) return;
    for (const wf of workflows) {
      const latest = runs
        .filter(r => runMatchesWorkflow(r, wf))
        .sort((a, b) => runNumber(b) - runNumber(a))[0];
      if (!latest) continue;
      (async () => {
        try {
          const resp = await fetchWithRetry(
            `/api/proxy/gitea/repos/${owner}/${repo}/actions/runs/${latest.id}/jobs`
          );
          if (!resp.ok) return;
          const data = await resp.json();
          // Gitea returns `workflow_jobs`; GitHub Actions returns `jobs`
          const jobs: Job[] = data.workflow_jobs ?? data.jobs ?? [];
          const statusMap: Record<string, string> = {};
          const stepMap: Record<string, StepStatus[]> = {};
          for (const j of jobs) {
            // Normalize name for matching: lowercase, treat hyphens = underscores = spaces
            const normalize = (s: string) => s.toLowerCase().replace(/[-_ ]/g, '');
            const jNorm = normalize(j.name);
            // Match job back to YAML key (try exact, then by display name, then normalized)
            const key =
              (j.name in wf.jobs ? j.name : undefined) ??
              Object.entries(wf.jobs).find(([k]) => normalize(k) === jNorm)?.[0] ??
              Object.entries(wf.jobs).find(([, jd]) => jd.name && normalize(jd.name) === jNorm)?.[0] ??
              j.name;
            statusMap[key] = (j.conclusion || j.status);
            if (j.steps?.length) stepMap[key] = j.steps;
          }
          // Fallback: if jobs API returned nothing, apply the run's own status to all YAML jobs
          if (jobs.length === 0 && (latest.conclusion || latest.status)) {
            const fallback = latest.conclusion || latest.status;
            for (const yKey of Object.keys(wf.jobs)) {
              statusMap[yKey] = fallback;
            }
          }
          setWfJobStatuses(prev => ({ ...prev, [wf.filename]: statusMap }));
          setWfJobStepStatuses(prev => ({ ...prev, [wf.filename]: stepMap }));
        } catch { /* ignore */ }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runs, workflows]);

  // ── Rerun handler ──────────────────────────────────────────────────────

  const rerun = useCallback(async (run: Run) => {
    try {
      const resp = await fetchWithRetry(
        `/api/proxy/gitea/repos/${owner}/${repo}/actions/runs/${run.id}/rerun`,
        { method: 'POST' }
      );
      if (!resp.ok) throw new Error(`${resp.status}`);
      setTimeout(fetchRuns, 1500);
    } catch (e: any) {
      // eslint-disable-next-line no-alert
      alert(`Failed to re-run: ${e.message}`);
    }
  }, [owner, repo, fetchRuns]);

  // ── Guard ──────────────────────────────────────────────────────────────

  if (!slug) {
    return (
      <EmptyState
        title="No Gitea Actions configured"
        description="Add gitea.kyndemo.live/repo-slug: <owner>/<repo> to your catalog-info.yaml to enable CI/CD."
        missing="field"
      />
    );
  }

  const isLoading = (runs === null && runsLoading) || (workflows === null && wfLoading);

  // Runs that don't belong to any workflow definition (orphaned)
  const orphanRuns = (runs && workflows && workflows.length > 0)
    ? runs.filter(r => !workflows.some(wf => runMatchesWorkflow(r, wf)))
    : [];

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <InfoCard
      title="Gitea Actions — Workflows"
      subheader={slug}
      action={
        <Tooltip title="Refresh">
          <IconButton onClick={() => { fetchRuns(); fetchWorkflows(); }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      }
      deepLink={{ title: 'Open in Gitea', link: `${giteaBaseUrl}/${slug}/actions` }}
    >
      {runsError && (
        <Box p={2}>
          <Typography variant="body2" color="error" gutterBottom>
            Failed to load runs: {runsError}
          </Typography>
          <Button variant="outlined" size="small" onClick={fetchRuns}>Retry</Button>
        </Box>
      )}

      {isLoading && (
        <Box p={2}>
          {[1, 2].map(i => (
            <Box key={i} mb={2}>
              <Skeleton variant="text" width="35%" height={28} />
              <Skeleton variant="rect" width="100%" height={90} style={{ marginTop: 8, borderRadius: 6 }} />
            </Box>
          ))}
        </Box>
      )}

      {!isLoading && workflows !== null && runs !== null && (
        <Box p={2}>
          {wfLoading && <CircularProgress size={16} style={{ marginBottom: 12 }} />}

          {workflows.length === 0 && (
            <Typography variant="body2" color="textSecondary" gutterBottom style={{ fontStyle: 'italic' }}>
              No workflow files found in .gitea/workflows/
            </Typography>
          )}

          {workflows.map(wf => (
            <WorkflowCard
              key={wf.filename}
              wf={wf}
              runs={runs}
              jobStatuses={wfJobStatuses[wf.filename] ?? {}}
              jobStepStatuses={wfJobStepStatuses[wf.filename] ?? {}}
              giteaBaseUrl={giteaBaseUrl}
              slug={slug}
              onRerun={rerun}
            />
          ))}

          {/* Show orphaned runs (no matching workflow definition loaded) */}
          {orphanRuns.length > 0 && (
            <Box mt={1}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Other Runs
              </Typography>
              <Table size="small" style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: 60 }} />
                  <col style={{ width: 130 }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 120 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 64 }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    {['Run', 'Workflow', 'Commit', 'Event', 'Status', 'Duration', 'Started', ''].map(h => (
                      <TableCell key={h} style={{ padding: '6px 12px', fontSize: '0.73rem', fontWeight: 700 }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orphanRuns.slice(0, 20).map(run => (
                    <TableRow key={run.id} hover>
                      <TableCell style={{ padding: '6px 12px', fontSize: '0.8rem' }}>#{runNumber(run)}</TableCell>
                      <TableCell style={{ padding: '6px 12px', fontSize: '0.8rem' }}>{wfNameFromRun(run)}</TableCell>
                      <TableCell style={{ padding: '6px 12px', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                        {headSha(run)?.slice(0, 7) ?? '\u2014'}
                      </TableCell>
                      <TableCell style={{ padding: '6px 12px', fontSize: '0.8rem' }}>
                        <Chip label={runEvent(run)} size="small" variant="outlined" style={{ fontSize: '0.7rem', height: 20 }} />
                      </TableCell>
                      <TableCell style={{ padding: '6px 12px' }}>
                        <StatusBadge status={run.status} conclusion={run.conclusion} />
                      </TableCell>
                      <TableCell style={{ padding: '6px 12px', fontSize: '0.8rem' }}>
                        {formatDuration(startedAt(run), stoppedAt(run), run.status === 'running' || run.status === 'in_progress' || run.status === 'waiting')}
                      </TableCell>
                      <TableCell style={{ padding: '6px 12px', fontSize: '0.8rem' }}>
                        {formatRelativeTime(startedAt(run) ?? createdAt(run))}
                      </TableCell>
                      <TableCell style={{ padding: '6px 12px' }}>
                        {htmlUrl(run) && (
                          <IconButton size="small" component="a" href={htmlUrl(run)!}
                            target="_blank" rel="noopener noreferrer">
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {runs.length === 0 && !runsError && (
            <EmptyState
              title="No workflow runs yet"
              description="Push to the repository or dispatch a workflow to see runs here."
              missing="data"
            />
          )}
        </Box>
      )}
    </InfoCard>
  );
};
